import { FC, FormEvent, useState } from "react";
import { Box, Button, Link, Stack, TextField, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { z } from "zod";
import { ROUTE, formatEmail } from "@/utils";
import { useForgotPassword } from "../../queries";

const emailSchema = z.object({
  email: z.string().min(1, "Field is required").email("Wrong format"),
});

type EmailFormData = z.infer<typeof emailSchema>;

interface RequestResetFormProps {
  onComplete: (email: string) => void;
}

export const RequestResetForm: FC<RequestResetFormProps> = ({ onComplete }) => {
  const { forgotPassword, isForgotPasswordLoading } = useForgotPassword();

  const [emailFormData, setEmailFormData] = useState<EmailFormData>({
    email: "",
  });
  const [emailTouched, setEmailTouched] = useState(false);

  const validateEmail = () => {
    const res = emailSchema.safeParse(emailFormData);
    if (res.success) {
      return undefined;
    }
    return res.error.format();
  };

  const emailErrors = validateEmail();
  const isEmailFormValid = !!emailFormData.email && !emailErrors;

  const handleRequestReset = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    forgotPassword({ email: emailFormData.email }, () =>
      onComplete(emailFormData.email),
    );
  };

  return (
    <Box component="form" width={"100%"} onSubmit={handleRequestReset}>
      <Stack justifyContent={"flex-start"} width={"100%"}>
        <Typography variant="h4" mt={4}>
          Forgot Password
        </Typography>
        <Typography variant="body2" sx={{ mb: 4 }}>
          Enter your email address to receive a reset code
        </Typography>
      </Stack>

      <TextField
        fullWidth
        variant="filled"
        autoComplete="email"
        sx={{ mb: 3 }}
        required
        placeholder="Email"
        label={
          emailTouched && emailErrors?.email
            ? emailErrors?.email?._errors[0]
            : "Email"
        }
        onBlur={() => setEmailTouched(true)}
        error={emailTouched ? !!emailErrors?.email?._errors.length : false}
        value={emailFormData.email}
        onChange={(e) => {
          setEmailFormData((prev) => ({
            ...prev,
            email: formatEmail(e.target.value),
          }));
        }}
        slotProps={{
          htmlInput: {
            autoCapitalize: "none",
          },
        }}
      />
      <Button
        fullWidth
        color="secondary"
        variant="contained"
        type="submit"
        loading={isForgotPasswordLoading}
        disabled={isForgotPasswordLoading || !isEmailFormValid}
      >
        Send Reset Code
      </Button>
      <Box mt={2} textAlign="center">
        <Link
          component={RouterLink}
          to={ROUTE.AUTH_LOGIN}
          sx={{
            color: "#FFFFFF",
            textDecoration: "none",
          }}
        >
          Back to Login
        </Link>
      </Box>
    </Box>
  );
};
